// Design system primitives — estratti dallo stile della LabelsPage (Hero tiles +
// Quick grid + StatCard) e riusati in tutte le pagine ridisegnate. Tema fisso
// chiaro, palette slate + accenti tenui pastello. Niente Ionic colors per
// avere controllo pieno sui contrasti.

import type { CSSProperties, MouseEvent, ReactNode } from 'react';
import { IonIcon } from '@ionic/react';
import { chevronForwardOutline } from 'ionicons/icons';

export const palette = {
  text: '#172033',
  textMuted: '#5d6a82',
  textFaint: '#7d899d',
  border: '#dce2ec',
  borderStrong: '#c8d0de',
  bg: '#f4f6fa',
  card: '#ffffff',
  highlight: '#fff6dc',
  accent: '#3157d5',
  danger: '#b42318',
  dangerSoft: '#fff0ee',
  warn: '#8b5a00',
  warnSoft: '#fff6dc',
  success: '#167a57',
  successSoft: '#eaf8f1',
} as const;

// Gradient ricette riusabili per HeroTile.
export const tileGradients = {
  blue: 'linear-gradient(135deg,#0066cc22,#8b5cf622)',
  teal: 'linear-gradient(135deg,#10b98122,#06b6d422)',
  amber: 'linear-gradient(135deg,#f59e0b22,#ef444422)',
  rose: 'linear-gradient(135deg,#ec489922,#f43f5e22)',
  slate: 'linear-gradient(135deg,#64748b22,#0f172a11)',
  green: 'linear-gradient(135deg,#22c55e22,#14b8a622)',
} as const;
export type TileGradient = keyof typeof tileGradients;

// ───────── Hero Tile (2-col) ─────────
export function HeroTile(props: {
  icon: string; // emoji o stringa singolo glifo
  title: string;
  subtitle?: string;
  gradient?: TileGradient;
  onClick: () => void;
  disabled?: boolean;
}): JSX.Element {
  const g = props.gradient ?? 'blue';
  const titleFit = props.title.length >= 14
    ? 'wide'
    : props.title.length >= 12
      ? 'compact'
      : 'regular';
  return (
    <button
      type="button"
      onClick={props.onClick}
      disabled={props.disabled}
      className="te-workflow-card"
      data-title-fit={titleFit}
      style={{
        backgroundColor: palette.card,
        backgroundImage: tileGradients[g],
        cursor: props.disabled ? 'not-allowed' : undefined,
        opacity: props.disabled ? 0.5 : 1,
      }}
    >
      <span className="te-workflow-card__icon">{props.icon}</span>
      <span className="te-workflow-card__title" title={props.title}>{props.title}</span>
      {props.subtitle && (
        <span className="te-workflow-card__description">{props.subtitle}</span>
      )}
    </button>
  );
}

// ───────── 2-col grid wrapper ─────────
export function TileGrid(props: { children: ReactNode; gap?: number; style?: CSSProperties }): JSX.Element {
  return (
    <div className="te-tile-grid" style={{ gap: props.gap ?? 12, ...props.style }}>
      {props.children}
    </div>
  );
}

// ───────── Section header ─────────
export function SectionHeader(props: { icon?: string; title: string; action?: ReactNode }): JSX.Element {
  return (
    <div className="te-section-heading">
      <h2>
        {props.icon && <span style={{ marginRight: 6 }}>{props.icon}</span>}
        {props.title}
      </h2>
      {props.action}
    </div>
  );
}

// ───────── StatCard (sostituisce IonCard) ─────────
// Uso primario: card con bordo a sinistra colorato e contenuto interno libero.
// Cliccabile (passa onClick) mostra chevron a destra.
export function StatCard(props: {
  accent?: 'blue' | 'amber' | 'rose' | 'slate' | 'green' | 'teal';
  children: ReactNode;
  onClick?: () => void;
  style?: CSSProperties;
}): JSX.Element {
  const accentColor: Record<NonNullable<typeof props.accent>, string> = {
    blue: palette.accent,
    amber: '#f59e0b',
    rose: palette.danger,
    slate: palette.textFaint,
    green: palette.success,
    teal: '#0d9488',
  };
  const accent = accentColor[props.accent ?? 'slate'];
  const interactive = !!props.onClick;
  return (
    <div
      role={interactive ? 'button' : undefined}
      tabIndex={interactive ? 0 : undefined}
      onClick={props.onClick}
      onKeyDown={
        interactive
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                props.onClick?.();
              }
            }
          : undefined
      }
      className="te-stat-card"
      style={{
        '--te-stat-accent': accent,
        ...props.style,
      } as CSSProperties}
    >
      {props.children}
      {interactive && (
        <IonIcon
          icon={chevronForwardOutline}
          style={{
            position: 'absolute',
            right: 12,
            top: '50%',
            transform: 'translateY(-50%)',
            color: palette.textFaint,
            fontSize: 18,
            pointerEvents: 'none',
          }}
        />
      )}
    </div>
  );
}

// ───────── StatRow: riga "etichetta · valore" dentro StatCard ─────────
export function StatRow(props: {
  label: string;
  value?: ReactNode;
  meta?: string;
  onClick?: (e: MouseEvent) => void;
  highlight?: boolean;
}): JSX.Element {
  return (
    <div
      onClick={props.onClick}
      style={{
        display: 'flex',
        alignItems: 'baseline',
        justifyContent: 'space-between',
        gap: 8,
        padding: '6px 0',
        borderBottom: `1px solid ${palette.border}`,
        cursor: props.onClick ? 'pointer' : 'default',
      }}
    >
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            fontSize: 14,
            fontWeight: 500,
            color: palette.text,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {props.label}
        </div>
        {props.meta && (
          <div style={{ fontSize: 12, color: palette.textFaint, marginTop: 2 }}>{props.meta}</div>
        )}
      </div>
      {props.value != null && (
        <div
          style={{
            fontSize: 14,
            fontWeight: 600,
            color: props.highlight ? palette.danger : palette.text,
            flexShrink: 0,
          }}
        >
          {props.value}
        </div>
      )}
    </div>
  );
}

// ───────── Empty hint ─────────
export function EmptyHint(props: { children: ReactNode }): JSX.Element {
  return (
    <div
      style={{
        fontSize: 13,
        color: palette.textFaint,
        fontStyle: 'italic',
        padding: '4px 0',
      }}
    >
      {props.children}
    </div>
  );
}

// ───────── Stat badge: numero grande in alto (es. "3 attive") ─────────
export function StatBadge(props: { count: number; label: string; tone?: 'danger' | 'warn' | 'neutral' }): JSX.Element {
  const tone = props.tone ?? 'neutral';
  const color = tone === 'danger' ? palette.danger : tone === 'warn' ? palette.warn : palette.textMuted;
  return (
    <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, marginBottom: 6 }}>
      <span style={{ fontSize: 26, fontWeight: 700, color }}>{props.count}</span>
      <span style={{ fontSize: 13, color: palette.textMuted }}>{props.label}</span>
    </div>
  );
}

// ───────── Page title (sotto IonHeader) ─────────
export function PageGreeting(props: { greeting: string; name?: string }): JSX.Element {
  return (
    <div style={{ padding: '16px 16px 4px' }}>
      <h1
        style={{
          margin: 0,
          fontSize: 22,
          fontWeight: 700,
          color: palette.text,
          letterSpacing: '-0.01em',
        }}
      >
        {props.greeting}
        {props.name ? <span style={{ color: palette.accent }}>, {props.name}</span> : null}
      </h1>
    </div>
  );
}
